import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import DynamicBreadcrumbs from '../../../components/Shared/Breadcrumb/index.tsx';
import { useSnackBar } from '../../../components/FlowBuilder/Common/SnackBar/index.tsx';
import useDebounce from '../../../components/Shared/Delay/index.tsx';
import { ROUTES } from '../../../constants/routes';
import { buildBreadcrumbs, createBreadcrumbNavigateHandler } from '../../../config/navigation/breadcrumbs';
import useLicenseList from '../hooks/useLicenseList.js';
import useLicenseSx from '../hooks/useLicenseSx.js';
import LicensePageHeader from '../components/LicensePageHeader.jsx';
import LicenseStatusTabs from '../components/LicenseStatusTabs.jsx';
import LicenseCardGrid from '../components/LicenseCardGrid.jsx';
import LicenseFormDrawer from '../components/LicenseFormDrawer.jsx';
import LicenseArchiveDialog from '../components/LicenseArchiveDialog.jsx';
import { licenseService } from '../services/licenseService.js';
import { normalizeLicense } from '../utils/normalizeLicense.js';
import { LICENSE_STATUS_TABS } from '../utils/licenseConstants.js';
import { FeatureThemeProvider } from '../../../theme/FeatureThemeProvider.jsx';

function getStatusCounts(licenses = []) {
  return LICENSE_STATUS_TABS.reduce((counts, tab) => {
    counts[tab.value] = licenses.filter((license) => license.status === tab.value).length;
    return counts;
  }, {});
}

export default function LicenseManagementPage() {
  return (
    <FeatureThemeProvider>
      <LicenseManagementPageContent />
    </FeatureThemeProvider>
  );
}

function LicenseManagementPageContent() {
  const styles = useLicenseSx();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const {
    licenses,
    isLoading,
    error,
    isUsingFallback,
    fetchLicenses,
    prependLicense,
    updateLicenseInList,
    archiveLicenseInList,
  } = useLicenseList();

  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);

  const debouncedSearch = useDebounce(search, 400);

  useEffect(() => {
    fetchLicenses(debouncedSearch);
  }, [debouncedSearch, fetchLicenses]);

  const statusCounts = useMemo(() => getStatusCounts(licenses), [licenses]);

  const filteredLicenses = useMemo(
    () => licenses.filter((license) => license.status === activeTab),
    [licenses, activeTab]
  );

  const activeTabMeta = useMemo(
    () => LICENSE_STATUS_TABS.find((tab) => tab.value === activeTab) ?? LICENSE_STATUS_TABS[0],
    [activeTab]
  );

  const breadcrumbsData = useMemo(
    () => buildBreadcrumbs([
      { label: 'Management', href: ROUTES.MANAGEMENT.PROFILE_LIST },
      { label: 'License Management' },
    ]),
    []
  );

  const openCreateForm = useCallback(() => {
    setEditingLicense(null);
    setIsFormOpen(true);
  }, []);

  const openEditForm = useCallback((license) => {
    setEditingLicense(license);
    setIsFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingLicense(null);
  }, []);

  const handleFormSuccess = useCallback(
    (rawLicense, { isEditMode } = {}) => {
      const license = normalizeLicense(rawLicense);

      if (isEditMode) {
        updateLicenseInList(license);
        if (LICENSE_STATUS_TABS.some((tab) => tab.value === license.status)) {
          setActiveTab(license.status);
        }
        showSnackBar('License updated successfully', 'success');
        return;
      }

      prependLicense(license);
      if (LICENSE_STATUS_TABS.some((tab) => tab.value === license.status)) {
        setActiveTab(license.status);
      }
      showSnackBar('License created successfully', 'success');
    },
    [prependLicense, showSnackBar, updateLicenseInList]
  );

  const handleArchiveConfirm = useCallback(async () => {
    if (!archiveTarget?.id) return;

    setIsArchiving(true);

    try {
      const res = await licenseService.archiveLicense(archiveTarget.id);

      if (res?.status === 200 || res?.status === 201) {
        const archived = normalizeLicense(res?.data?.data ?? res?.data ?? {
          ...archiveTarget,
          status: 'archived',
        });
        archiveLicenseInList(archived);
        setActiveTab('archived');
        showSnackBar('License archived successfully', 'success');
        setArchiveTarget(null);
        return;
      }

      throw new Error(res?.data?.message || 'Failed to archive license');
    } catch (err) {
      showSnackBar(
        err?.response?.data?.message || err?.message || 'Failed to archive license',
        'error'
      );
    } finally {
      setIsArchiving(false);
    }
  }, [archiveLicenseInList, archiveTarget, showSnackBar]);

  const handleRetry = useCallback(() => {
    fetchLicenses(debouncedSearch);
  }, [debouncedSearch, fetchLicenses]);

  return (
    <Box sx={styles.pageRoot}>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={createBreadcrumbNavigateHandler(navigate)}
      />

      <LicensePageHeader
        search={search}
        onSearchChange={setSearch}
        onCreate={openCreateForm}
        count={filteredLicenses.length}
        statusLabel={activeTabMeta.label}
      />

      {error && (
        <Alert
          severity={isUsingFallback ? 'warning' : 'error'}
          sx={{ mb: 2.5 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <LicenseStatusTabs
        value={activeTab}
        onChange={setActiveTab}
        counts={statusCounts}
      />

      <Box sx={styles.tabPanel}>
        <LicenseCardGrid
          licenses={filteredLicenses}
          isLoading={isLoading}
          isSearching={Boolean(debouncedSearch)}
          statusTab={activeTab}
          onCreate={openCreateForm}
          onEdit={openEditForm}
          onArchive={setArchiveTarget}
        />
      </Box>

      <LicenseFormDrawer
        open={isFormOpen}
        license={editingLicense}
        onClose={closeForm}
        onSuccess={handleFormSuccess}
      />

      <LicenseArchiveDialog
        open={Boolean(archiveTarget)}
        license={archiveTarget}
        isSubmitting={isArchiving}
        onClose={() => {
          if (!isArchiving) setArchiveTarget(null);
        }}
        onConfirm={handleArchiveConfirm}
      />
    </Box>
  );
}
